import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import CheckmarkSmall from '@/assets/icons/checkmark-small';
import GiftBox from '@/assets/icons/gift-box';
import XIcon from '@/assets/icons/x-small';
import { Switch } from '@/components/Switch';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { useBonusRemovesPromoSheet } from '@/hooks/use-bonus-removes-promo-sheet';
import { useWalletStore } from '@/hooks/use-wallet';
import { CriterionType, Currency } from '@/types/api.generated';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';
import { isTruthy } from '@/utils/is-truthy';

import { CheckoutWidget } from './CheckoutWidget';

export const BetrBucksWidget = memo(() => {
    const { t } = useTranslation('betslip_pickem');
    const selectedPromo = useBetslipStore(state => state.selectedPromo);
    const setSelectedPromo = useBetslipStore(state => state.actions.setSelectedPromo);
    const betrBucks = useWalletStore(state => state.betrBucks);
    const setCurrency = useBetslipStore(state => state.actions.setCurrency);
    const gameMode = useBetslipStore(state => state.activeGameMode);
    const currency = useBetslipStore(state => state.currency);
    const isBetrBucksEnabled = currency === Currency.Fre;
    const showBonusRemovesPromoSheet = useBonusRemovesPromoSheet();
    const cashOnlyPromoType = selectedPromo?.promotion.criteria.some(
        criteria => isTruthy(criteria.value) && criteria.type === CriterionType.CashOnly
    );

    const handleUseBetrBucks = () => {
        if (isBetrBucksEnabled) {
            setCurrency(Currency.Usd, gameMode);
        } else if (betrBucks > 0) {
            if (selectedPromo && cashOnlyPromoType) {
                showBonusRemovesPromoSheet({
                    message: t('betr_bucks_will_remove_promo'),
                    confirmLabel: t('turn_on_betr_bucks'),
                    onConfirm: () => {
                        BetrAnalytics.trackEvent(AnalyticsEvent.BETR_BUCKS_SHEET_TURN_ON_BETR_BUCKS, {
                            promoId: selectedPromo?.promotion.id,
                        });
                        setSelectedPromo(undefined);
                        setCurrency(Currency.Fre, gameMode);
                    },
                    onKeep: () => {
                        BetrAnalytics.trackEvent(AnalyticsEvent.BETR_BUCKS_SHEET_KEEP_APPLIED_PROMO, {
                            promoId: selectedPromo?.promotion.id,
                        });
                        setCurrency(Currency.Usd, gameMode);
                    },
                });
            } else {
                setCurrency(Currency.Fre, gameMode);
            }
        }
        triggerHapticFeedback();
    };

    return (
        <CheckoutWidget>
            <Row justifyContent={'space-between'} alignItems={'center'}>
                <Row alignItems={'center'}>
                    <GiftBox />
                    <Text
                        ml={'s16'}
                        letterSpacing={-0.43}
                        lineHeight={24}
                        variant={'bodyLarge'}
                        color={ds.colorToken.text.primary}
                    >
                        {t('use_betr_bucks')} ${betrBucks}
                    </Text>
                </Row>
                <Switch
                    val={isBetrBucksEnabled}
                    handleOnPress={handleUseBetrBucks}
                    inactiveTrackColor={ds.colorValue.background.neutral.strong}
                    activeTrackColor={ds.colorValue.background.brand.emphasis}
                    onIcon={<CheckmarkSmall color={ds.colorValue.background.brand.emphasis} />}
                    offIcon={<XIcon />}
                    // Touch target size has to be 56x56
                    // The actual size of the Switch is 44x28, so we need to increase touchable area using hitSlop:
                    // Width: 56-44=12 => add 6px to left and right
                    // Height: 56-28=28 => add 14px to top and bottom
                    hitSlop={{ top: 14, bottom: 14, left: 6, right: 6 }}
                />
            </Row>
        </CheckoutWidget>
    );
});
